import type { Locale } from '@change-corgi/config/locales';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type {
	HomepageTopImpactStoriesDataQuery,
	HomepageTopImpactStoriesDataQueryVariables,
	HomepageV3ConfigQuery,
} from './getV3Data.graphql';
import { HomepageTopImpactStoriesData } from './getV3Data.graphql';

type ImpactStoryConfig = NonNullable<
	NonNullable<HomepageV3ConfigQuery['featureConfigs']['capybaraHomepageV3']['perLocaleConfig']>[Locale]
>['topImpactStories'][0];

type Props = {
	topImpactStoriesConfig: readonly ImpactStoryConfig[];
	utilityContext: UtilityContext;
};

export type ImpactStory = {
	petition: {
		id: string;
		slug: string;
		signatureCount: string;
		isVictory: boolean;
	};
	badgeTitle?: string;
	summary: string;
	imageUrl: string;
	testimonialUrl?: string;
};

export async function getTopImpactStories({
	topImpactStoriesConfig,
	utilityContext: {
		gql: { fetch },
		errorReporter,
	},
}: Props): Promise<ImpactStory[]> {
	const impactStoryPromises = topImpactStoriesConfig.map(async (story) => {
		const { data } = await fetch<HomepageTopImpactStoriesDataQuery, HomepageTopImpactStoriesDataQueryVariables>({
			query: HomepageTopImpactStoriesData,
			variables: { petitionId: story.petitionId },
			rejectOnError: false,
		});

		if (!data?.petition) {
			void errorReporter.report(new Error(`No data returned for impact story petition ${story.petitionId}`));
			return null;
		}

		return {
			petition: {
				id: story.petitionId,
				slug: data.petition.slug,
				signatureCount: data.petition.signatureState.signatureCount.displayedLocalized,
				isVictory: data.petition.status === 'VICTORY',
			},
			...(story.badgeTitle && { badgeTitle: story.badgeTitle }),
			summary: story.summary,
			imageUrl: story.imageUrl,
			...(story.testimonialUrl && { testimonialUrl: story.testimonialUrl }),
		};
	});

	return (await Promise.all(impactStoryPromises)).filter(Boolean);
}
